import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ConnectAppDownloadStrip } from "@/components/connect/ConnectAppDownloadStrip";

import { ConnectServicesRow } from "@/components/connect/ConnectServicesRow";
import { ConnectTrustRow } from "@/components/connect/ConnectTrustRow";
import { ConnectFooter } from "@/components/connect/ConnectFooter";
import { AppStoreBadgesOfficial } from "@/components/connect/AppStoreBadgesOfficial";
import { DialerLanguageToggle } from "@/components/connect/DialerLanguageToggle";
import { CurrencySwitcher } from "@/components/connect/CurrencySwitcher";
import { useTranslation } from "react-i18next";
import { SEOHead } from "@/components/seo/SEOHead";
import { PAGE_SEO, getCanonicalUrl, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, TELECOM_SERVICE_SCHEMA, SOFTWARE_APP_SCHEMA } from "@/config/seo";
import { Logo } from "@/components/ui/Logo";

/**
 * Public landing page (web only — native users are gated in NativeRootGate).
 *
 * Copy is canonical: mirrors the app's 7 messages and value props verbatim
 * per WEBSITE_redesign_SPEC_for_lovable.md §2/§4. Do not paraphrase.
 * The wedge: "only the caller needs elefa; the person they call just
 * answers any phone." Lead with it. No browser dialer CTAs — every call
 * action routes to the app download.
 */
export default function ConnectHome() {
  const { t } = useTranslation("common");

  return (
    <div className="min-h-screen bg-background safe-area-top relative overflow-hidden">
      {/* Decorative branded background - radial elefa-orange glows on a clean canvas */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.18),transparent_55%),radial-gradient(ellipse_at_bottom_left,hsl(var(--elefa-gold)/0.12),transparent_50%),radial-gradient(ellipse_at_bottom_right,hsl(var(--elefa-teal)/0.10),transparent_50%)]" />
        <div className="absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-primary/5 to-transparent" />
      </div>
      <SEOHead
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        keywords={PAGE_SEO.home.keywords}
        canonicalUrl={getCanonicalUrl('/')}
        structuredData={[ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, TELECOM_SERVICE_SCHEMA, SOFTWARE_APP_SCHEMA]}
      />
      {/* Minimal Navigation */}
      <nav className="container pt-1 pb-3 sm:pt-2 sm:pb-4 flex flex-wrap items-center justify-between gap-2">
        <Logo size="sm" withIcon fullName />
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          <CurrencySwitcher />
          <DialerLanguageToggle />
          <Link to="/connect-portal/auth">
            <Button variant="ghost" size="sm">{t("logIn")}</Button>
          </Link>
          <Link to="/connect-portal/auth">
            <Button size="sm">{t("getStarted")}</Button>
          </Link>
        </div>
      </nav>

      <main id="main-content">
        {/* Hero — canonical copy from spec §4 */}
        <section className="py-16 sm:min-h-[75vh] flex flex-col items-center justify-center px-4">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto text-center space-y-6 sm:space-y-8">
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold tracking-tight leading-[1.05]">
                {t("web.landing.heroTitle")}
              </h1>

              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto">
                {t("web.landing.heroSubtitle")}
              </p>

              <div className="flex flex-col items-center gap-4 pt-4">
                <AppStoreBadgesOfficial />
                <Link
                  to="/connect-portal/rates"
                  className="text-sm font-medium text-primary hover:underline underline-offset-4"
                >
                  {t("web.landing.seeRates")}
                </Link>
              </div>

              <p className="text-xs text-muted-foreground/80 pt-2">
                {t("web.landing.heroFinePrint")}
              </p>
            </div>
          </div>
        </section>

        {/* The wedge — the ONE differentiator */}
        <section className="py-14 sm:py-20 bg-muted/40 border-y">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-5">
              {t("web.landing.wedgeTitle")}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              {t("web.landing.wedgeBody")}
            </p>
          </div>
        </section>

        <ConnectAppDownloadStrip />
        
        <ConnectServicesRow />
        <ConnectTrustRow />

        {/* Emergency Services notice — keep, required disclosure */}
        <div className="container mx-auto px-4 py-10 max-w-2xl">
          <p className="text-xs text-center text-muted-foreground">
            <span className="text-destructive font-semibold">{t("important")}</span>{" "}
            {t("emergencyWarning")}
          </p>
        </div>
      </main>

      <ConnectFooter />
    </div>
  );
}


